import React, { useEffect, useMemo, useState } from 'react';
import * as api from '../services/apiService';
import { BACKEND_URL } from '../utils/constants';

/**
 * EditSubmissionModal
 * Reusable user-facing edit modal for a single inspection submission.
 *
 * Uses UUID-based user edit + user-approve endpoints (DEV-first).
 */
const EditSubmissionModal = ({ isVisible, inspection, currentUser, onClose, onSaved }) => {
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null); // For full-size image view
  const [additionalImages, setAdditionalImages] = useState([]); // For new images

  const submittedBy = useMemo(() => {
    return inspection?.submitted_by || (typeof currentUser === 'string' ? currentUser : '') || 'User';
  }, [inspection, currentUser]);

  const approverEmail = useMemo(() => {
    return (typeof currentUser === 'string' && currentUser.includes('@')) ? currentUser : (inspection?.user_id || undefined);
  }, [currentUser, inspection]);

  useEffect(() => {
    if (isVisible && inspection) {
      setEditFormData({
        business_name: inspection.business_name || '',
        business_address: inspection.business_address || '',
        business_phone: inspection.business_phone || '',
        site_street_address: inspection.site_street_address || '',
        site_city: inspection.site_city || '',
        site_state: inspection.site_state || '',
        site_zip: inspection.site_zip || '',
        notes: inspection.notes || '',
        location: inspection.location || '',
        // AI-captured dates
        last_inspection_date: inspection.last_inspection_date || '',
        next_due_date: inspection.next_due_date || '',
        // Provider info
        current_provider: inspection.current_provider || '',
        working_service_company_name: inspection.working_service_company_name || '',
        working_service_company_phone: inspection.working_service_company_phone || ''
      });
    }
  }, [isVisible, inspection]);

  // Thumbnail URL (for the small preview)
  const thumbnailUrl = useMemo(() => {
    if (inspection?.thumbnail_url) return `${BACKEND_URL}${inspection.thumbnail_url}`;
    if (inspection?.image_url) return `${BACKEND_URL}${inspection.image_url}`;
    if (inspection?.id) return `${BACKEND_URL}/api/inspections/${inspection.id}/image?thumbnail=true`;
    return '';
  }, [inspection]);
  
  // Full-size URL (for expanded view - no thumbnail parameter)
  const fullImageUrl = useMemo(() => {
    if (inspection?.image_url) return `${BACKEND_URL}${inspection.image_url}`;
    if (inspection?.id) return `${BACKEND_URL}/api/inspections/${inspection.id}/image`;
    return '';
  }, [inspection]);

  if (!isVisible || !inspection) return null;

  const save = async ({ approve, reject } = { approve: false, reject: false }) => {
    setIsSaving(true);
    try {
      const payload = { ...editFormData, submitted_by: submittedBy };
      const editRes = await api.updateInspection(inspection.id, payload);

      if (approve) {
        await api.userApproveInspection(inspection.id, {
          approver_name: submittedBy,
          approver_email: approverEmail
        });
      }

      if (reject) {
        await api.userRejectInspection(inspection.id, {
          rejector_name: submittedBy,
          rejector_email: approverEmail
        });
      }

      const updatedInspection = editRes?.inspection
        ? editRes.inspection
        : {
            ...inspection,
            ...editFormData,
            ...(approve
              ? {
                  status: 'approved',
                  approval_source: 'user',
                  approved_by_user: true,
                  approved_by: approverEmail || submittedBy,
                  approval_timestamp: new Date().toISOString(),
                  approval_status: 'approved'
                }
              : reject
              ? {
                  status: 'rejected',
                  approval_source: 'user',
                  rejected_by_user: true,
                  rejected_by: approverEmail || submittedBy,
                  rejection_timestamp: new Date().toISOString(),
                  approval_status: 'rejected'
                }
              : {})
          };

      if (onSaved) onSaved(updatedInspection);
      if (onClose) onClose();
    } catch (error) {
      console.error('❌ EditSubmissionModal save failed:', error);
      // Use native alert for now - toast system requires hook context
      // TODO: Pass showToast as prop for better UX
      window.alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10001] flex items-center justify-center p-4 sm:p-6"
      onClick={(e) => {
        // Only close if clicking the backdrop directly, not any child elements
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-[var(--pbfs-navy)] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing modal
      >
        {/* Header */}
        <div className="pt-8 px-6 pb-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-white text-xl font-bold">Edit Submission</h3>
            <p className="text-white/60 text-sm">
              Update business details and notes for this scan.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Body - Scrollable content area */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto max-h-[calc(85vh-180px)]">
          {/* Submitted By - context info */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="text-white/70 text-sm">Submitted By</div>
            <div className="text-white font-semibold mt-1 flex items-center gap-2">
              <span className="text-[var(--pbfs-green)]">★</span>
              {submittedBy}
              {inspection.submitted_by_email && submittedBy !== inspection.submitted_by_email && (
                <span className="text-white/50 text-sm ml-1">({inspection.submitted_by_email})</span>
              )}
            </div>
          </div>
          
          {/* Thumbnail + AI Dates */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Thumbnail - Clickable to expand to FULL size */}
              <div className="flex-shrink-0">
                <div className="text-white/70 text-sm mb-2">Tag Image</div>
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt="Tag thumbnail"
                    onClick={() => setExpandedImage(fullImageUrl || thumbnailUrl)}
                    className="w-full h-32 object-cover rounded-lg border border-white/10 bg-black/20 cursor-pointer hover:border-[var(--pbfs-green)] transition-all shadow-lg"
                    loading="lazy"
                    title="Click to view full size"
                  />
                ) : (
                  <div className="w-full h-32 rounded-lg border border-white/10 bg-black/20 flex items-center justify-center text-white/40 text-xs">
                    No image
                  </div>
                )}
              </div>

              {/* Last Inspection Date (AI Analysis) */}
              <div className="flex-1">
                <div className="text-white/70 text-sm mb-2">Last Inspection Date (AI)</div>
                <input
                  type="date"
                  value={(editFormData.last_inspection_date || '').slice(0, 10)}
                  onChange={(e) => setEditFormData({ ...editFormData, last_inspection_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                  style={{ colorScheme: 'dark' }}
                  placeholder="mm/dd/yyyy"
                />
                <div className="text-white/40 text-xs mt-1">
                  Date from tag
                </div>
              </div>

              {/* Next Due Date (Calculated) */}
              <div className="flex-1">
                <div className="text-white/70 text-sm mb-2">Next Due Date</div>
                <input
                  type="date"
                  value={(editFormData.next_due_date || '').slice(0, 10)}
                  onChange={(e) => setEditFormData({ ...editFormData, next_due_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                  style={{ colorScheme: 'dark' }}
                  placeholder="mm/dd/yyyy"
                />
                <div className="text-white/40 text-xs mt-1">
                  Calculated expiration
                </div>
              </div>
            </div>
          </div>
          
          {/* Expanded Image Modal */}
          {expandedImage && (
            <div
              className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
              onClick={() => setExpandedImage(null)}
            >
              <div className="relative max-w-full max-h-full flex items-center justify-center">
                <img
                  src={expandedImage}
                  alt="Full size tag"
                  className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
                {/* Close button - Always visible on image */}
                <button
                  onClick={() => setExpandedImage(null)}
                  className="absolute top-3 right-3 w-12 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-2xl transition-all hover:scale-110 active:scale-95"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/70 text-sm">Business Name</label>
              <input
                type="text"
                value={editFormData.business_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, business_name: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm">Business Phone</label>
              <input
                type="text"
                value={editFormData.business_phone || ''}
                onChange={(e) => setEditFormData({ ...editFormData, business_phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
              />
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm">Business Address (optional)</label>
            <input
              type="text"
              value={editFormData.business_address || ''}
              onChange={(e) => setEditFormData({ ...editFormData, business_address: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm">Site Address</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
              <input
                type="text"
                value={editFormData.site_street_address || ''}
                onChange={(e) => setEditFormData({ ...editFormData, site_street_address: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="Street"
              />
              <input
                type="text"
                value={editFormData.site_city || ''}
                onChange={(e) => setEditFormData({ ...editFormData, site_city: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="City"
              />
              <input
                type="text"
                value={editFormData.site_state || ''}
                onChange={(e) => setEditFormData({ ...editFormData, site_state: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="State"
              />
              <input
                type="text"
                value={editFormData.site_zip || ''}
                onChange={(e) => setEditFormData({ ...editFormData, site_zip: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="ZIP"
              />
            </div>
          </div>

          {/* Provider Information */}
          <div>
            <label className="text-white/70 text-sm">Current Provider (AI)</label>
            <input
              type="text"
              value={editFormData.current_provider || ''}
              onChange={(e) => setEditFormData({ ...editFormData, current_provider: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
              placeholder="Provider company name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/70 text-sm">Provider Name (override)</label>
              <input
                type="text"
                value={editFormData.working_service_company_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, working_service_company_name: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="Override provider name (optional)"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm">Provider Phone (override)</label>
              <input
                type="text"
                value={editFormData.working_service_company_phone || ''}
                onChange={(e) => setEditFormData({ ...editFormData, working_service_company_phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="Override provider phone (optional)"
              />
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm">Notes</label>
            <textarea
              value={editFormData.notes || ''}
              onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              rows={3}
              className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)] resize-none"
              placeholder="Any additional notes..."
            />
          </div>
        </div>

        {/* Footer - Compact 3-button layout for mobile */}
        <div className="px-3 pt-4 pb-8 sm:pb-6 border-t border-white/10 flex items-stretch gap-2">
          {/* Cancel - Compact icon button */}
          <button
            onClick={onClose}
            disabled={isSaving}
            className="w-12 sm:w-14 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white text-xl rounded-lg transition-all disabled:opacity-50"
            title="Cancel"
          >
            ✕
          </button>
          
          {/* Approve - Primary action (green) */}
          <button
            onClick={() => save({ approve: true, reject: false })}
            disabled={isSaving}
            className="flex-1 py-3 px-3 sm:px-4 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white font-bold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 active:scale-[0.98]"
          >
            <span className="text-lg">✓</span>
            <span className="text-sm sm:text-base">{isSaving ? 'Saving…' : 'Approve'}</span>
          </button>
          
          {/* Reject - Destructive action (red) */}
          <button
            onClick={() => save({ approve: false, reject: true })}
            disabled={isSaving}
            className="flex-1 py-3 px-3 sm:px-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 active:scale-[0.98]"
          >
            <span className="text-lg">✗</span>
            <span className="text-sm sm:text-base">{isSaving ? 'Saving…' : 'Reject'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSubmissionModal;


